import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Seo from "../components/Seo";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export function TemplatesPublic() {
  const { locale, t } = useI18n();
  const [data, setData] = useState({ templates: [], categories: [], total: 0 });
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const p = new URLSearchParams();
    if (cat) p.set("category", cat);
    if (q) p.set("q", q);
    api.get(`/templates?${p}`).then(({ data }) => setData(data)).catch(() => {});
  }, [cat, q]);

  return (
    <div className="pt-16" data-testid="templates-page">
      <Seo
        title={locale === "fr" ? "150 templates vidéo IA — Studio Movies IA" : "150 AI video templates — Studio Movies IA"}
        description={t("tpl.sub")}
        locale={locale}
        path={`/${locale}/templates`}
        altPath={`/${locale === "en" ? "fr" : "en"}/templates`}
      />
      <section className="px-6 pb-14 pt-20 lg:px-10 lg:pt-28">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">Template library · {data.total}</p>
          <h1 className="mt-4 font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
            {t("tpl.title")}
          </h1>
          <p className="mt-6 max-w-[600px] text-base text-[#9194a6] md:text-lg">{t("tpl.sub")}</p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border border-[#2a2c3c] px-4 py-2">
              <Search className="h-3.5 w-3.5 text-[#8288a0]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="template-search"
                placeholder={locale === "fr" ? "Rechercher…" : "Search…"}
                className="w-40 bg-transparent text-[13px] outline-none placeholder:text-[#8288a0]"
              />
            </div>
            <button
              onClick={() => setCat("")}
              data-testid="template-cat-all"
              className={`rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors duration-200 ${
                !cat ? "border-white bg-white text-black" : "border-[#2a2c3c] text-[#9194a6] hover:text-white"
              }`}
            >
              {locale === "fr" ? "Tous" : "All"}
            </button>
            {data.categories.map((c) => (
              <button
                key={c.name}
                onClick={() => setCat(c.name)}
                data-testid={`template-cat-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors duration-200 ${
                  cat === c.name ? "border-white bg-white text-black" : "border-[#2a2c3c] text-[#9194a6] hover:text-white"
                }`}
              >
                {c.name} <span className="mono opacity-50">{c.count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-28 lg:px-10">
        <div className="mx-auto grid max-w-[1400px] gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data.templates.map((x) => (
            <Link
              key={x.id}
              to={`/${locale}/templates/${x.slug}`}
              data-testid={`template-card-${x.slug}`}
              className="hot-border group overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]"
            >
              <div className="relative overflow-hidden">
                <img
                  src={x.thumbnail}
                  alt={x.title}
                  loading="lazy"
                  className="h-[170px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {x.premium && (
                  <span className="mono absolute right-3 top-3 rounded-full bg-black/70 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-[#ff4b2b] backdrop-blur">
                    Premium
                  </span>
                )}
              </div>
              <div className="p-5">
                <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#ff4b2b]">{x.category}</p>
                <p className="mt-2 font-display text-base font-medium">{x.title}</p>
                <p className="mt-2 line-clamp-2 text-[12.5px] leading-relaxed text-[#9194a6]">
                  {x.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function TemplateDetail() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const [x, setX] = useState(null);

  useEffect(() => {
    api.get(`/templates/${slug}`).then(({ data }) => setX(data)).catch(() => setX(false));
  }, [slug]);

  if (!x) return <div className="px-6 pt-40 text-center text-[#9194a6]">…</div>;

  return (
    <div className="pt-16" data-testid="template-detail">
      <Seo title={x.seo_title} description={x.seo_description} locale={locale}
           path={`/${locale}/templates/${x.slug}`} image={x.thumbnail} />
      <section className="px-6 pb-24 pt-20 lg:px-10 lg:pt-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div className="overflow-hidden rounded-2xl border border-[#212230]">
            <img src={x.thumbnail} alt={x.title} className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="overline">{x.category}</p>
            <h1 className="mt-4 font-display text-4xl font-semibold lg:text-5xl">{x.title}</h1>
            <p className="mt-6 text-base leading-relaxed text-[#9194a6]">{x.description}</p>

            <div className="mt-9 space-y-4 rounded-xl border border-[#212230] bg-[#0b0b11] p-6">
              <p className="overline">{locale === "fr" ? "Structure du prompt" : "Prompt structure"}</p>
              <p className="mono text-[12px] leading-relaxed text-[#c0c3d0]">{x.prompt_structure}</p>
            </div>

            <div className="mono mt-6 grid grid-cols-2 gap-4 text-[11px] text-[#9194a6]">
              <p>RATIOS · {x.supported_ratios?.join(" ")}</p>
              <p>DURATIONS · {x.supported_durations?.join("s ")}s</p>
              <p>~{x.credit_hint} CREDITS</p>
              <p>{x.premium ? "PREMIUM" : "FREE"}</p>
            </div>

            <Link
              to="/studio/create"
              data-testid="template-use-cta"
              className="btn-amber mt-10 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
            >
              {locale === "fr" ? "Utiliser ce template" : "Use this template"}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {x.related?.length > 0 && (
          <div className="mx-auto mt-24 max-w-[1400px]">
            <p className="overline">{locale === "fr" ? "Dans la même catégorie" : "More in this category"}</p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {x.related.map((r) => (
                <Link
                  key={r.id}
                  to={`/${locale}/templates/${r.slug}`}
                  className="overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]"
                >
                  <img src={r.thumbnail} alt={r.title} className="h-24 w-full object-cover" loading="lazy" />
                  <p className="p-3 text-[12.5px] font-medium">{r.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
